import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Plus, Globe, Lock, Trash2, Edit2, BookOpen, Users, X, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Navigation from "../components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "most_items", label: "Most items" },
  { value: "most_followers", label: "Most followers" },
];

const sortCollections = (list, sortBy) => {
  const arr = [...(list || [])];
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  const byDate = (a, b) => (a.created_at || "").localeCompare(b.created_at || "");
  switch (sortBy) {
    case "name_desc": return arr.sort((a, b) => byName(b, a));
    case "newest": return arr.sort((a, b) => byDate(b, a));
    case "oldest": return arr.sort(byDate);
    case "most_items": return arr.sort((a, b) => (b.item_count || 0) - (a.item_count || 0));
    case "most_followers": return arr.sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0));
    default: return arr.sort(byName);
  }
};

const MyCollectionsPage = ({ user }) => {
  const [collections, setCollections] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPublic, setNewPublic] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [tab, setTab] = useState("mine");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("collections_sort") || "name_asc");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const matchesQuery = (c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q);
  };

  const changeSort = (v) => {
    setSortBy(v);
    localStorage.setItem("collections_sort", v);
  };

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCollections = useCallback(async () => {
    try {
      const [mine, fol] = await Promise.all([
        axios.get(`${API}/collections/my`, { headers }),
        axios.get(`${API}/collections/following`, { headers }),
      ]);
      setCollections(mine.data || []);
      setFollowing(fol.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  const createCollection = async () => {
    if (!newName.trim()) { toast.error("Enter a name"); return; }
    try {
      await axios.post(`${API}/collections`, {
        name: newName, description: newDesc, is_public: newPublic,
      }, { headers });
      toast.success("Collection created");
      setShowCreate(false);
      setNewName(""); setNewDesc(""); setNewPublic(false);
      fetchCollections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creating collection");
    }
  };

  const deleteCollection = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    try {
      await axios.delete(`${API}/collections/${id}`, { headers });
      toast.success("Collection deleted");
      fetchCollections();
    } catch (err) { toast.error("Error deleting"); }
  };

  const renameCollection = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/collections/${id}`, { name: editName }, { headers });
      setEditingId(null);
      fetchCollections();
    } catch (err) { toast.error("Error renaming"); }
  };

  const unfollowCollection = async (id) => {
    try {
      await axios.post(`${API}/collections/${id}/follow`, {}, { headers });
      toast.success("Unfollowed");
      fetchCollections();
    } catch (err) { toast.error("Error unfollowing"); }
  };

  const CollectionCard = ({ coll, isOwner }) => (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 hover:shadow-sm transition-all cursor-pointer group"
      onClick={() => navigate(`/collections/${coll.id}`)}
      data-testid={`collection-${coll.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {editingId === coll.id ? (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                className="h-8 text-sm" autoFocus onKeyDown={(e) => e.key === "Enter" && renameCollection(coll.id)} />
              <Button size="sm" variant="ghost" onClick={() => renameCollection(coll.id)}><Check className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors" style={{ fontFamily: "Fraunces, serif" }}>
              {coll.name}
            </h3>
          )}
          {coll.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{coll.description}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            {coll.is_public ? (
              <span className="flex items-center gap-1 text-green-600"><Globe className="w-3 h-3" />Public</span>
            ) : (
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Private</span>
            )}
            <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{coll.item_count || 0} items</span>
            {coll.follower_count > 0 && (
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{coll.follower_count} followers</span>
            )}
            {!isOwner && <span>by {coll.user_name || coll.user_email}</span>}
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" onClick={() => { setEditingId(coll.id); setEditName(coll.name); }} data-testid={`edit-${coll.id}`}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteCollection(coll.id)} data-testid={`delete-${coll.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  if (!user) {
    return (
      <div className="min-h-screen bg-sky-50">
        <Navigation user={user} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Fraunces, serif" }}
            data-testid="collections-title"
          >
            Reading Collections
          </h1>
          <section className="text-slate-600 text-sm leading-relaxed max-w-3xl mb-8" data-testid="collections-intro-public">
            <p className="mb-3">
              Collections are curated reading lists you can build from any document in The Archive.
              Group papers by research topic, project, course, or client — for example, a collection of <strong>hydrometallurgy studies</strong>, a bibliography for a <strong>metallurgical thesis</strong>, or a set of <strong>mining safety reports</strong>.
              Make a collection <strong>public</strong> to share it with the community, or keep it <strong>private</strong> for your own reference.
              Follow other users&apos; public collections to discover documents adjacent to your field.
            </p>
            <p className="mb-3">
              Every collection can be edited, renamed, or reordered at any time. Documents added to a public collection remain accessible to your subscribers, and you can turn a collection private again with a single click.
              Public collections appear in a shared feed so that other researchers browsing The Archive can find carefully curated bibliographies rather than sifting through the full 8,000+ document catalog on their own.
            </p>
            <p className="text-slate-500 text-xs">
              <strong>Popular topics on The Archive:</strong> extractive metallurgy · pyrometallurgy · hydrometallurgy · flotation and mineral processing · cobalt and copper leaching · solvent extraction · electrowinning · mine geotechnics · rock mechanics · mine ventilation · industrial chemistry · reactor design · thermodynamics of metals · materials science · corrosion · environmental engineering · mine safety · sustainability in mining.
            </p>
          </section>
          <div className="text-center pt-8">
            <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">Sign in to create your own reading lists</p>
            <Button
              className="mt-4 bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => navigate("/login")}
              data-testid="collections-signin-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="collections-title">
              My Collections
            </h1>
            <p className="text-sm text-slate-500 mt-1">Organize your reading lists and discover others</p>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setShowCreate(true)} data-testid="create-collection-btn">
            <Plus className="w-4 h-4 mr-2" />New Collection
          </Button>
        </div>

        <section className="mb-8 text-slate-600 text-sm leading-relaxed max-w-4xl" data-testid="collections-intro">
          <p className="mb-3">
            Collections are curated reading lists you can build from any document in The Archive.
            Group papers by research topic, project, course, or client — for example, a collection of <strong>hydrometallurgy studies</strong>, a bibliography for a <strong>metallurgical thesis</strong>, or a set of <strong>mining safety reports</strong>.
            Make a collection <strong>public</strong> to share it with the community, or keep it <strong>private</strong> for your own reference.
            Follow other users&apos; public collections to discover documents adjacent to your field.
          </p>
          <p className="mb-3">
            Every collection can be edited, renamed, or reordered at any time. Documents added to a public collection remain accessible to your subscribers, and you can turn a collection private again with a single click.
            Public collections appear in a shared feed so that other researchers browsing The Archive can find carefully curated bibliographies rather than sifting through the full 8,000+ document catalog on their own.
          </p>
          <p className="text-slate-500 text-xs">
            <strong>Popular topics on The Archive:</strong> extractive metallurgy · pyrometallurgy · hydrometallurgy · flotation and mineral processing · cobalt and copper leaching · solvent extraction · electrowinning · mine geotechnics · rock mechanics · mine ventilation · industrial chemistry · reactor design · thermodynamics of metals · materials science · corrosion · environmental engineering · mine safety · sustainability in mining.
          </p>
        </section>

        {/* Create dialog */}
        {showCreate && (
          <div className="bg-white border border-orange-200 rounded-lg p-5 mb-6 space-y-4" data-testid="create-collection-form">
            <h3 className="font-semibold text-slate-900">Create Collection</h3>
            <Input placeholder="Collection name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="collection-name-input" />
            <textarea placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" rows={2}
              data-testid="collection-desc-input" />
            <div className="flex items-center gap-2">
              <input type="checkbox" id="isPublic" checked={newPublic} onChange={(e) => setNewPublic(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
              <label htmlFor="isPublic" className="text-sm text-slate-700">Make this collection public</label>
            </div>
            <div className="flex gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={createCollection} data-testid="save-collection-btn">Create</Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Tabs + sort */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6 border-b border-slate-200">
          <div className="flex gap-1">
            <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "mine" ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              onClick={() => setTab("mine")} data-testid="tab-mine">
              My Collections ({collections.length})
            </button>
            <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "following" ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              onClick={() => setTab("following")} data-testid="tab-following">
              Following ({following.length})
            </button>
            <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "discover" ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              onClick={() => setTab("discover")} data-testid="tab-discover">
              Discover
            </button>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Input
              placeholder="Search collections..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-48 text-xs bg-white"
              data-testid="collections-search"
            />
            <span className="text-xs text-slate-500 whitespace-nowrap">Sort by:</span>
            <Select value={sortBy} onValueChange={changeSort}>
              <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="collections-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        {tab === "mine" && (
          <div className="space-y-3">
            {collections.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No collections yet</p>
                <p className="text-sm text-slate-400 mt-1">Create one to start organizing your reading</p>
              </div>
            ) : sortCollections(collections.filter(matchesQuery), sortBy).length === 0 ? (
              <div className="text-center py-12" data-testid="no-search-results">
                <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No collections match &quot;{query}&quot;</p>
              </div>
            ) : (
              sortCollections(collections.filter(matchesQuery), sortBy).map((c) => <CollectionCard key={c.id} coll={c} isOwner />)
            )}
          </div>
        )}

        {tab === "following" && (
          <div className="space-y-3">
            {following.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">Not following any collections</p>
                <p className="text-sm text-slate-400 mt-1">Browse public collections in the Discover tab</p>
              </div>
            ) : (
              sortCollections(following.filter(matchesQuery), sortBy).map((c) => (
                <div key={c.id} className="relative">
                  <CollectionCard coll={c} isOwner={false} />
                  <Button variant="outline" size="sm" className="absolute top-4 right-4 text-xs"
                    onClick={(e) => { e.stopPropagation(); unfollowCollection(c.id); }}>
                    Unfollow
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "discover" && <DiscoverTab headers={headers} user={user} onFollow={fetchCollections} sortBy={sortBy} query={query} />}
      </div>
    </div>
  );
};

/* ── Discover Tab ── */
const DiscoverTab = ({ headers, user, onFollow, sortBy, query }) => {
  const [collections, setCollections] = useState([]);
  const navigate = useNavigate();

  const fetchPublic = useCallback(async (q) => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const resp = await axios.get(`${API}/collections/public?${params}`, { headers });
      setCollections(resp.data.collections || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchPublic(query), 300);
    return () => clearTimeout(t);
  }, [fetchPublic, query]);

  const handleFollow = async (id) => {
    try {
      const resp = await axios.post(`${API}/collections/${id}/follow`, {}, { headers });
      toast.success(resp.data.following ? "Following!" : "Unfollowed");
      fetchPublic(query);
      onFollow();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <div className="space-y-3">
        {collections.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">No public collections found</p>
          </div>
        ) : (
          sortCollections(collections, sortBy).map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 transition-all cursor-pointer flex items-start justify-between"
              onClick={() => navigate(`/collections/${c.id}`)} data-testid={`public-collection-${c.id}`}>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{c.name}</h3>
                {c.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  <span>by {c.user_name || c.user_email}</span>
                  <span><BookOpen className="w-3 h-3 inline" /> {c.item_count} items</span>
                  <span><Users className="w-3 h-3 inline" /> {c.follower_count} followers</span>
                </div>
              </div>
              {c.user_id !== user?.id && (
                <Button variant="outline" size="sm" className="shrink-0 text-xs ml-4"
                  onClick={(e) => { e.stopPropagation(); handleFollow(c.id); }}
                  data-testid={`follow-${c.id}`}>
                  {(c.followers || []).includes(user?.id) ? "Unfollow" : "Follow"}
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MyCollectionsPage;
